import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function AdminSellDown() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", investor: "", pool_type: "DA", target_amount: 0, status: "building", notes: "" });

  const load = () => api.get("/admin/selldown/pools").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/selldown/pools", { ...form, target_amount: Number(form.target_amount) });
      toast.success("Pool created"); setOpen(false); load();
      setForm({ name: "", investor: "", pool_type: "DA", target_amount: 0, status: "building", notes: "" });
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-selldown-page">
      <div className="flex justify-between items-center mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Loan Sell-Down Pools</h1>
          <p className="text-sm text-slate-600 mt-1">Direct Assignment (DA), Pass-Through Certificates (PTC) and Business Correspondent (BC) pools.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="selldown-add-btn"><Plus size={16} className="mr-1" /> New Pool</Button>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">Pool</th><th className="text-left px-4 py-3">Investor</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Target</th><th className="text-left px-4 py-3">Pooled</th><th className="text-left px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                <td className="px-4 py-3">{p.investor}</td>
                <td className="px-4 py-3 font-mono">{p.pool_type}</td>
                <td className="px-4 py-3 font-mono">{formatINR(p.target_amount)}</td>
                <td className="px-4 py-3 font-mono">{formatINR(p.pooled_amount)} <span className="text-xs text-slate-500">({p.pooled_count} loans)</span></td>
                <td className="px-4 py-3 capitalize"><span className="badge-progress px-2 py-0.5 rounded-full text-xs">{p.status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No pools yet. Create your first sell-down pool.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="selldown-dialog">
          <DialogHeader><DialogTitle>Create sell-down pool</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Pool name</Label><Input data-testid="selldown-name" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} /></div>
            <div className="col-span-2"><Label>Investor</Label><Input data-testid="selldown-investor" value={form.investor} onChange={e => setForm(p => ({...p, investor: e.target.value}))} /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.pool_type} onValueChange={v => setForm(p => ({...p, pool_type: v}))}>
                <SelectTrigger data-testid="selldown-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="DA">Direct Assignment (DA)</SelectItem><SelectItem value="PTC">PTC</SelectItem><SelectItem value="BC">BC</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Target amount (₹)</Label><Input type="number" data-testid="selldown-target" value={form.target_amount} onChange={e => setForm(p => ({...p, target_amount: e.target.value}))} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea data-testid="selldown-notes" value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="selldown-save-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
